import Vue from 'vue';
import axios from 'axios';

// Canal/evento de WebSockets para refrescar los badges en tiempo real.
const COUNTERS_CHANNEL = 'system-counters';
const COUNTERS_EVENT = '.counters.actualizados';

const state = {
    counters: {},
    lastFetch: null,
    isLoading: false
};

const getters = {
    // Obtener todos los contadores
    getAllCounters: (state) => state.counters,
    
    // Obtener un contador específico
    getCounter: (state) => (key) => {
        const value = state.counters[key];
        return typeof value === 'number' ? value : Number(value) || 0;
    },
    
    // Verificar si hay datos en cache (menos de 5 minutos)
    shouldRefreshCounters: (state) => {
        if (!state.lastFetch) return true;
        return (Date.now() - state.lastFetch) > (5 * 60 * 1000); // 5 minutos
    },
    
    // Estado de carga
    isLoadingCounters: (state) => state.isLoading
};

const mutations = {
    SET_COUNTERS(state, counters) {
        state.counters = { ...state.counters, ...counters };
        state.lastFetch = Date.now();
    },

    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },

    UPDATE_COUNTER(state, { key, value }) {
        Vue.set(state.counters, key, value);
    },

    CLEAR_COUNTERS(state) {
        state.counters = {};
        state.lastFetch = null;
    }
};

const actions = {
    // Cargar contadores con cache inteligente
    async loadCounters({ commit, getters, dispatch }, forceRefresh = false) {
        // Si no forzamos refresh y tenemos datos recientes, usamos cache
        if (!forceRefresh && !getters.shouldRefreshCounters) {
            return getters.getAllCounters;
        }

        commit('SET_LOADING', true);
        
        try {
            const url = process.env.VUE_APP_API_BASE_URL + '/api/system-counter';
            const response = await axios.get(url);
            // console.log("respuesta counters:", response.data.data);
            commit('SET_COUNTERS', response.data.data);
            
            return response.data;
        } catch (error) {
            console.error('Error loading counters:', error);
            if (Object.keys(getters.getAllCounters).length === 0) {
                commit('SET_COUNTERS', {});
            }
        } finally {
            commit('SET_LOADING', false);
        }
    },

    // Forzar refresco de contadores
    async refreshCounters({ dispatch }) {
        return await dispatch('loadCounters', true);
    },

    // Actualizar un contador específico (útil para optimismo)
    updateCounter({ commit }, { key, value }) {
        commit('UPDATE_COUNTER', { key, value });
    },

    // Suscripción global a WebSockets: el backend emite 'counters.actualizados'
    // cada vez que un contador global cambia (vía Observers). Aplicamos el mapa
    // recibido sin necesidad de volver a consultar el endpoint. Es seguro
    // llamarla aunque Echo no esté disponible (no-op).
    subscribeCounters({ commit }) {
        if (typeof window === 'undefined' || !window.Echo) return;
        try {
            window.Echo.channel(COUNTERS_CHANNEL).listen(COUNTERS_EVENT, (e) => {
                const counters = e?.counters ?? e;
                if (counters && typeof counters === 'object') {
                    // SET_COUNTERS hace merge, por lo que preserva contadores
                    // por-usuario (ej: pre_sofonos_mios) que no vienen en el broadcast.
                    commit('SET_COUNTERS', counters);
                }
            });
        } catch (error) {
            // Echo es opcional; si falla, el fallback por polling mantiene los badges.
            console.warn('No se pudo suscribir a system-counters:', error);
        }
    },

    unsubscribeCounters() {
        if (typeof window === 'undefined' || !window.Echo) return;
        try {
            window.Echo.leave(COUNTERS_CHANNEL);
        } catch (error) {
            // noop
        }
    },
};

export default {    
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};